import styles from './styles.module.scss';
import UserPaymentDetailsFormContentUserKycCard from './UserKycCard';
import { UserPaymentDetailsFormContentFields } from './Fields';
import { UserPaymentDetailsFormContentProps } from './props';
import {
  useCoallescedUserPaymentTypeId,
  useTranslation,
} from '../../../../../../../hooks';
import { Nullable } from '@starsoft/common/interfaces';

/**
 * UserPaymentDetailsFormContent Component
 * This component is responsible for rendering the content of the user payment details form.
 * It includes the user's KYC card, a description, and the form fields.
 *
 * @param {UserPaymentDetailsFormContentProps} props - The properties for the component.
 * @param {string} props.userImage - The URL or path to the user's image to be displayed in the KYC card.
 *
 * @returns {JSX.Element} The rendered component displaying the user payment details form content.
 *
 * @example
 * // Usage example:
 * <UserPaymentDetailsFormContent userImage="path/to/user/image.jpg" />
 */
export default function UserPaymentDetailsFormContent({
  userImage,
}: UserPaymentDetailsFormContentProps) {
  // Hook to get the translation function for the 'common' namespace
  const { t } = useTranslation('common');

  // Hook to get the coalesced user payment type ID, which can be null
  const typeId: Nullable<string> = useCoallescedUserPaymentTypeId();

  // Render the component structure
  return (
    <div className={styles.container}>
      {/* Render the user's KYC card with the provided image */}
      <UserPaymentDetailsFormContentUserKycCard image={userImage} />

      {/* Render the description using the translation function and type ID */}
      <div className={styles.container__description}>
        {t('create_user_payment_main_description', {
          key: t(`${typeId}_user_payment_description`),
        })}
      </div>

      {/* Render the form fields component */}
      <UserPaymentDetailsFormContentFields />
    </div>
  );
}
